import { isFunction, merge } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Reads an object from local storage.
 */
function readObjectFromLocalStorage<T>(key: string): T {
  try {
    const value = localStorage.getItem(key) || '{}';
    return JSON.parse(value) as T;
  } catch (e) {
    return {} as T;
  }
}

/**
 * Uses local storage to store the specified value. Uses a write-through cache.
 */
export default function useLocalStorage<T>(key: string, defaultValue: T | (() => T)) {
  const [value, setValue] = useState(() =>
    merge(isFunction(defaultValue) ? defaultValue() : defaultValue, readObjectFromLocalStorage<T>(key))
  );
  const valueRef = useRef(value);

  const setStoredValue = useCallback(
    (nextValue: T | ((previousValue: T) => T)) => {
      const next = isFunction(nextValue) ? nextValue(valueRef.current) : nextValue;
      valueRef.current = next;
      localStorage.setItem(key, JSON.stringify(next));
      setValue(next);
    },
    [key]
  );

  const updateValue = useCallback(
    (update: Partial<T>) => setStoredValue((previousValue) => ({ ...previousValue, ...update })),
    [setStoredValue]
  );

  useEffect(() => {
    valueRef.current = value;
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setStoredValue, updateValue] as const;
}
